<template>
    <LoginForm v-if="!isAuthenticated" />
    <SidebarLayout v-else>
        <main class="flex w-full flex-1 flex-col items-center justify-center">
            <LoadingOrError
                v-if="isErrorLocations || isErrorOrders"
                :loadingText="$t('page.chooseLocation.loading.message')"
                :errorTitle="$t('page.chooseLocation.errors.title')"
                :isError="isErrorLocations || isErrorOrders"
            >
                <b>{{ $t('page.chooseLocation.errors.loading.subtitle') }}</b>
                <p>{{ $t('page.chooseLocation.errors.loading.message') }}</p>
            </LoadingOrError>
            <section v-else class="flex h-screen w-full flex-col items-center justify-center">
                <div
                    class="flex w-full max-w-lg grow flex-col items-center justify-center gap-4 p-4"
                >
                    <i class="pi pi-face-smile text-4xl text-secondary" />
                    <h1>{{ $t('location.hi', { name: userName }) }}</h1>
                    <MultiSelect
                        v-model="selected"
                        display="chip"
                        :options="locations"
                        option-label="name"
                        filter
                        data-testid="location-chooser"
                        :placeholder="$t('location.choose')"
                        :max-selected-labels="3"
                        :selected-items-label="$t('location.selectedItems', { count: '{count}' })"
                        class="w-full"
                        :disabled="isLoadingLocations || isPendingLocations"
                        :loading="isLoadingLocations || isPendingLocations"
                        auto-filter-focus
                        show-clear
                        @value-change="updateOrders"
                    />
                </div>
                <div class="flex h-36 flex-col items-center justify-center">
                    <template
                        v-if="
                            (isPendingOrders ||
                                isLoadingOrders ||
                                isPendingLocations ||
                                isLoadingLocations) &&
                            selected?.length
                        "
                    >
                        <LoadingAnimation :text="$t('location.loadingInfos')" />
                    </template>
                    <template
                        v-else-if="
                            !!selected?.length &&
                            ((locationInfo?.partnerCount || 0) === 0 ||
                                (locationInfo?.orderCount || 0) === 0) &&
                            !isPendingLocations
                        "
                    >
                        {{ $t('location.none') }}
                    </template>
                    <template v-else-if="selected?.length && orders?.length">
                        <I18nT scope="global" keypath="location.status" tag="span">
                            <template #count>
                                <b>{{ locationInfo?.orderCount }} {{ $t('order.default') }}</b>
                            </template>
                            <template #partner>
                                <b>{{ locationInfo?.partnerCount }} {{ $t('partner.default') }}</b>
                            </template>
                        </I18nT>
                        <Button
                            data-testid="start-edit-orders-button"
                            class="mt-4"
                            :label="$t('actions.ordersEdit')"
                            @click="router.push({ name: RouteNames.ToProcess })"
                        />
                    </template>
                </div>
            </section>
        </main>
    </SidebarLayout>
</template>

<script setup lang="ts">
    import { useLocations } from '@/api/location';
    import { useGetOrderList } from '@/api/order';
    import { QUERY_ORDERS } from '@/constants';
    import SidebarLayout from '@/layouts/SidebarLayout.vue';
    import { RouteNames } from '@/router/routeNames';
    import useAuthStore from '@/stores/useAuthStore';
    import useLocationsStore from '@/stores/useLocationsStore';
    import { getUserName } from '@/utils/authStoreUtils';
    import { useQueryClient } from '@tanstack/vue-query';
    import { storeToRefs } from 'pinia';
    import { computed } from 'vue';
    import { useRouter } from 'vue-router';

    const { isAuthenticated } = storeToRefs(useAuthStore());

    const queryClient = useQueryClient();
    const router = useRouter();

    const userName = computed<string>(() => getUserName());
    const { selected, locationInfo } = storeToRefs(useLocationsStore());

    const {
        data: locations,
        isLoading: isLoadingLocations,
        isPending: isPendingLocations,
        isError: isErrorLocations,
    } = useLocations();

    const {
        data: orders,
        isLoading: isLoadingOrders,
        isPending: isPendingOrders,
        isError: isErrorOrders,
    } = useGetOrderList();

    const updateOrders = () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_ORDERS] });
    };
</script>
